<!--
  THIS IS FOR INTEGER NUMBERS ONLY
  FOR FLOATS/ DECIMALS: USE DECIMAL INPUT

  Precision limitation (JavaScript Number):
  - Values are parsed as Number (IEEE-754). Safe integer range is
    [-9,007,199,254,740,991, 9,007,199,254,740,991]. Beyond this, precision is lost
    and values may round unexpectedly.
  - Examples beyond the safe range:
      1999999999999999 → adding another 9 may produce 20000000000000000
      200000000000000000000 → adding one more digit may collapse to a small number when parsed
  - If we need precise handling of arbitrarily large integers in the future, we should use a string/BigInt-based input
    or constrain via an appropriate `max`.

  Example usage:

  <IntegerInput
    id='my_integer_input'
    bind:value={my_integer_value}
    min={1}
    max={10}
  />
 -->
<script>
  import ButtonLabel from "#lib/ButtonLabel.svelte";
  import t from "#lib/i18n.svelte.js";

  let {
    ref = $bindable(),
    value = $bindable(),
    /** @type {string} */
    placeholder,
    id,
    class: css_class,
    /** @type {number} integer */
    min,
    /** @type {number} integer */
    max,
    /** @type {number} integer */
    step = 1,
    /** @type {number} Multiplier when shift key is pressed (default 10) */
    shift_multiplier = 10,
    /** @type {boolean} */
    disabled,
    /** @type {boolean} */
    show_spinners = true,
    /** @type {string} Accessible name when there is no associated <label> */
    aria_label,
    onkeydown,
    onchange,
    onblur,
    /**
     * Defines the visual style of the input. Combines surface and color variants.
     * @type {string} - Space-separated combination of the following:
     * Surface: 'outlined' | 'underlined' | 'ghostly'
     * Color: 'canvas' | 'system'
     * @example "brand subtle-outlined"
     */
    variant = 'brand subtle-outlined',
  } = $props();

  /** @type {boolean} Flash warning when value is clamped */
  let show_clamp_warning = $state(false);

  function clamp_value(val) {
    if (typeof min === 'number' && val < min) return min;
    if (typeof max === 'number' && val > max) return max;
    return val;
  }

  function update_value(new_value, should_clamp = true, trigger_onchange = true) {
    const parsed = parseInt(new_value, 10);
    if (isNaN(parsed)) {
      value = undefined;
    } else {
      value = should_clamp ? clamp_value(parsed) : parsed;
    }
    if (ref) ref.value = value === undefined ? '' : value; // Avoid literal 'undefined' in the input
    if (trigger_onchange) {
      onchange && onchange(value);
    }
  }

  function increment(shift_pressed = false) {
    // Determine starting value: current value > numeric placeholder > min > 0
    // This prevents "undefined" from appearing when incrementing an empty field
    const base_value = (value !== undefined && value !== null && !isNaN(value)) 
      ? value  // Use current value if it exists and is valid
      : (placeholder !== undefined && !isNaN(parseFloat(placeholder)) 
          ? parseFloat(placeholder)  // Use placeholder if it's a valid number
          : (min ?? 0));  // Fall back to min (if defined) or 0
    const effective_step = shift_pressed ? step * shift_multiplier : step;
    update_value(base_value + effective_step);
  }

  function decrement(shift_pressed = false) {
    // Determine starting value: current value > numeric placeholder > min > 0
    // This prevents "undefined" from appearing when decrementing an empty field
    const base_value = (value !== undefined && value !== null && !isNaN(value)) 
      ? value  // Use current value if it exists and is valid
      : (placeholder !== undefined && !isNaN(parseFloat(placeholder)) 
          ? parseFloat(placeholder)  // Use placeholder if it's a valid number
          : (min ?? 0));  // Fall back to min (if defined) or 0
    const effective_step = shift_pressed ? step * shift_multiplier : step;
    update_value(base_value - effective_step);
  }

  function handle_keydown(e) {
    // HACK: we make sure that keydown events don't bubble up to the parent and trigger for instance
    // navigation in the Container on arrow up/down.
    // Could this become a problem somewhere?
    e.stopPropagation();
    
    // Prevent typing minus sign if min >= 0 (negative values not allowed)
    if (e.key === '-' && typeof min === 'number' && min >= 0) {
      e.preventDefault();
      return;
    }
    
    switch(e.key) {
      case 'ArrowUp':
        e.preventDefault();
        increment(e.shiftKey);
        break;
      case 'ArrowDown':
        e.preventDefault();
        decrement(e.shiftKey);
        break;
      case 'Enter':
        e.preventDefault();
        if (value === undefined || value === null || isNaN(value)) {
          const default_value = (placeholder !== undefined && !isNaN(parseFloat(placeholder)))
            ? parseFloat(placeholder)
            : (min ?? 0);
          update_value(default_value);
        }
        break;
    }
    onkeydown && onkeydown(e);
  }

  function handle_blur(e) {
    const input_value = e.target.value.trim();
    
    // Handle invalid/incomplete input (empty or just "-")
    if (input_value === '' || input_value === '-') {
      // Clear the input field
      if (ref) ref.value = '';
      value = undefined;
      onchange && onchange(value);
      onblur && onblur(e);
      return;
    }
    
    // Enforce min/max constraints on blur
    if (value !== undefined && value !== null && !isNaN(value)) {
      const clamped = clamp_value(value);
      if (clamped !== value) {
        // Value was clamped, show warning flash
        value = clamped;
        if (ref) ref.value = value;
        show_clamp_warning = true;
        setTimeout(() => {
          show_clamp_warning = false;
        }, 600); // Animation duration
        onchange && onchange(value);
      }
    }
    onblur && onblur(e);
  }
</script>

<div class="integer-input {css_class}">
  <input
    bind:this={ref}
    {id}
    aria-label={aria_label}
    class='text-control selected-outline-on-focus {variant} {variant.includes('outlined') ? 'border-1' : ''}'
    class:clamp-warning={show_clamp_warning}
    type="text"
    inputmode="numeric"
    pattern="[0-9\-]*"
    autocomplete="off"
    {placeholder}
    value={value === undefined ? '' : value}
    {disabled}
    oninput={e => {  
      e.stopPropagation();
      
      // Filter out non-numeric characters except minus sign
      let filtered_value = e.target.value.replace(/[^0-9\-]/g, '');
      
      // Remove minus sign if min >= 0
      if (typeof min === 'number' && min >= 0) {
        filtered_value = filtered_value.replace(/\-/g, '');
      }
      
      // Ensure only one minus sign and only at the beginning
      const has_minus = filtered_value.includes('-');
      if (has_minus) {
        filtered_value = filtered_value.replace(/\-/g, '');
        if (e.target.value[0] === '-') {
          filtered_value = '-' + filtered_value;
        }
      }
      
      // Update the input field with filtered value
      if (e.target.value !== filtered_value) {
        e.target.value = filtered_value;
      }
      
      // Handle empty input
      if (filtered_value === '') {
        value = undefined;
        onchange && onchange(value);
        return;
      }
      
      // Allow partial input like "-" during typing, will be cleaned up on blur
      if (filtered_value === '-') {
        return;
      }
      
      const new_value = parseInt(filtered_value, 10);
      if (!isNaN(new_value)) {
        // Don't clamp during typing - only enforce on blur
        update_value(new_value, false);
      }
    }}
    onkeydown={handle_keydown}
    onblur={handle_blur}
  />
  {#if show_spinners && !disabled}
    <div class="spinners">
      <button 
        type="button"
        class="spinner-button"
        onclick={(e) => increment(e.shiftKey)}
        disabled={disabled || (max !== undefined && value >= max)}
        aria-label={t('increment_value')}
      >
        <ButtonLabel variant="system ghostly" start_icon="small-arrow-up" />
      </button>
      <button 
        type="button"
        class="spinner-button"
        onclick={(e) => decrement(e.shiftKey)}
        disabled={disabled || (min !== undefined && value <= min)}
        aria-label={t('decrement_value')}
      >
        <ButtonLabel variant="system ghostly" start_icon="small-arrow-down" />
      </button>
    </div>
  {/if}
</div>

<style lang='postcss'>
  .integer-input {
    display: inline-flex;
    align-items: stretch;
    min-height: var(--input-min-height);
    &:not(.fit-content) {
      width: 100%;
    }
    input {
      width: 100%;
      min-width: var(--input-min-height);
      text-align: right;
      field-sizing: content;
    }
    &:has(.spinners){
      input {
        /* padding-inline-end: var(--size-2); */
      }
    }
  }
  .spinners {
    display: flex;
    flex-direction: column;
    min-height: var(--input-min-height);
  }
  .spinner-button :global(.button-label) {
    height: calc(var(--input-min-height) / 2);
    padding: 0;
    border-radius: 0;
    :global(.start-icon-container), :global(.end-icon-container) {
      min-height: calc(var(--input-min-height) / 2);
    }
  }
  .spinner-button:focus-visible :global(.button-label){
    outline: none !important;
    background-color: var(--editing-200);
  }
  .spinner-button:first-child {
    /* divider line */
    box-shadow: inset 0 -1px 0 var(--color-default-stroke);
  }
  
  .clamp-warning {
    animation: clamp-flash 0.6s ease-in-out;
  }
  
  @keyframes clamp-flash {
    0%, 100% {
      background-color: transparent;
    }
    50% {
      background-color: var(--color-warning-bg);
    }
  }
</style>
